import React, { useState } from "react";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";

export interface ButtonOption {
  id: string;
  label: string;
  value?: any;
}

export interface ButtonGroupProps {
  buttons: ButtonOption[];
  defaultSelected?: string;
  onSelectionChange?: (selectedId: string, selectedButton: ButtonOption) => void;
  orientation?: "horizontal" | "vertical";
  fullWidth?: boolean;
}

/**
 * A single-choice button row, used for the reference format pickers.
 *
 * The bordered ToggleGroup underneath: the selected look comes from ui/toggle-group
 * rather than from colours declared here, so it is right in both colour schemes. An
 * earlier implementation styled a button row by hand with literal greys and
 * `!important`, which is exactly what dark mode cannot survive.
 */
export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  buttons,
  defaultSelected,
  onSelectionChange,
  orientation = "horizontal",
  fullWidth = true,
}) => {
  const [selectedId, setSelectedId] = useState<string>(defaultSelected ?? "");

  return (
    <ToggleGroup
      type="single"
      variant="bordered"
      value={selectedId}
      orientation={orientation}
      fullWidth={fullWidth}
      aria-label="button group"
      onValueChange={(id) => {
        // An exclusive group reports "" when the selected button is pressed again. A
        // format is required, so a re-press keeps the selection rather than clearing it.
        if (!id) return;
        setSelectedId(id);
        const button = buttons.find((option) => option.id === id);
        if (button) onSelectionChange?.(id, button);
      }}
    >
      {buttons.map((button) => (
        <ToggleGroupItem key={button.id} value={button.id}>
          {button.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};
